/**
 * ThekenbelegungTab.jsx
 * Vitrinen-Baukasten – 12-Spalten-Raster-System
 * - Alle Rollen: Vitrine ansehen (Zeit-Slot automatisch oder manuell)
 * - Admin/Stationsleitung: Konfigurator-Modus (Zonen, Raster, Produkte)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Plus, Trash2, Settings, X, Check, ChevronDown, ChevronUp,
  Clock, Pencil, FileText, Loader2, BookOpen, GripVertical, Eye, Package, Image as ImageIcon, MapPin
} from 'lucide-react';
import axios from 'axios';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN = () => localStorage.getItem('token');
const authH = () => ({ Authorization: `Bearer ${TOKEN()}` });

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

const DEFAULT_TIME_SLOTS = [
  { key: 'morgens',  label: 'Morgens',  starts_at: '06:00' },
  { key: 'mittags',  label: 'Mittags',  starts_at: '12:00' },
  { key: 'abends',   label: 'Abends',   starts_at: '17:00' },
];

function getActiveSlotKey(timeSlots) {
  if (!timeSlots?.length) return 'morgens';
  const now = new Date();
  const cur = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
  const sorted = [...timeSlots].sort((a, b) => b.starts_at.localeCompare(a.starts_at));
  const active = sorted.find(ts => ts.starts_at <= cur);
  return active?.key || timeSlots[0]?.key || 'morgens';
}

// Zählt belegte Zellen pro Slot-Key über alle Zonen einer Vitrine
function countSlotProducts(zones, slotKey) {
  let count = 0;
  for (const zone of zones || []) {
    for (const cell of Object.values(zone.slots || {})) {
      if (cell?.[slotKey]) count++;
    }
  }
  return count;
}

// Findet den "besten" Slot: zuerst den zeitlich aktuellen (falls belegt),
// sonst den letzten Slot vor dem aktuellen der noch Produkte hat.
function getSmartSlotKey(timeSlots, zones) {
  if (!timeSlots?.length) return 'morgens';
  const autoKey = getActiveSlotKey(timeSlots);

  // Wenn der automatisch erkannte Slot belegt ist → direkt verwenden
  if (countSlotProducts(zones, autoKey) > 0) return autoKey;

  // Sonst: absteigend durch alle Slots suchen, die VOR dem aktuellen liegen
  const sorted = [...timeSlots].sort((a, b) => b.starts_at.localeCompare(a.starts_at));
  for (const ts of sorted) {
    if (countSlotProducts(zones, ts.key) > 0) return ts.key;
  }

  // Fallback: erster Slot in der Konfiguration
  return timeSlots[0]?.key || 'morgens';
}

function buildEmptySlots(cols, rows) {
  const s = {};
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++)
      s[`${r}_${c}`] = { morgens: null, mittags: null, abends: null };
  return s;
}

// ─── Zeit-Slot-Bar ────────────────────────────────────────────────────────────
function TimeSlotBar({ timeSlots, activeKey, onSelect, slotCounts = {} }) {
  const autoKey = getActiveSlotKey(timeSlots);
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="time-slot-bar">
      {timeSlots.map(ts => {
        const count = slotCounts[ts.key] ?? 0;
        const isEmpty = count === 0;
        const isActive = activeKey === ts.key;
        return (
          <button
            key={ts.key}
            onClick={() => !isEmpty && onSelect(ts.key)}
            disabled={isEmpty}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
              ${isActive && !isEmpty
                ? 'bg-amber-500 text-white border-amber-500'
                : isEmpty
                  ? 'border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed bg-gray-50 dark:bg-gray-900/30'
                  : 'border-gray-300 dark:border-gray-600 text-muted-foreground hover:border-amber-400'
              }`}
            data-testid={`time-slot-${ts.key}`}
            aria-disabled={isEmpty}
            title={isEmpty ? `${ts.label}: keine Produkte hinterlegt` : `${ts.label}: ${count} Produkt${count !== 1 ? 'e' : ''}`}
          >
            <Clock className={`h-3 w-3 ${isEmpty ? 'opacity-40' : ''}`} />
            <span className={isEmpty ? 'opacity-40' : ''}>{ts.label}</span>
            <span className={`text-xs ${isEmpty ? 'opacity-30' : 'opacity-70'}`}>ab {ts.starts_at}</span>
            {/* Produkt-Badge */}
            {!isEmpty && (
              <span className={`text-[10px] font-semibold rounded-full px-1 leading-none tabular-nums ${
                isActive ? 'bg-white/25 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}>
                {count}
              </span>
            )}
            {/* Grüner Punkt = aktuell aktive Schicht */}
            {ts.key === autoKey && !isEmpty && (
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 ml-0.5 shrink-0" title="Aktuelle Schicht" />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Produkt-Cell (Ansicht) ───────────────────────────────────────────────────
function ProductCellView({ productId, products, onClickPdf }) {
  const prod = products.find(p => p.id === productId);
  if (!productId || !prod) {
    return <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-gray-600 text-xs">–</div>;
  }
  const hasPdf = !!prod.pdf_url;
  const displayImg = prod.image_url
    ? `${API}/api/backshop/products/${prod.id}/image?token=${TOKEN()}`
    : prod.thumbnail_url
      ? `${API}${prod.thumbnail_url}?token=${TOKEN()}`
      : null;
  return (
    <button
      className={`relative w-full h-full flex flex-col items-center justify-start gap-1 p-1.5 rounded transition-colors text-center ${
        hasPdf ? 'hover:bg-amber-50 dark:hover:bg-amber-900/20 cursor-pointer active:bg-amber-100' : 'cursor-default'
      }`}
      onClick={() => hasPdf && onClickPdf(prod)}
      title={hasPdf ? `PDF öffnen: ${prod.name}` : prod.name}
      data-testid={`product-cell-${productId}`}
    >
      {/* Bild-Bereich – obere ~60% der Zelle */}
      <div className="w-full flex-1 flex items-center justify-center min-h-0 overflow-hidden">
        {displayImg
          ? <img src={displayImg} alt={prod.name} className="w-full h-full object-contain" onError={e => { e.target.style.display='none'; }} />
          : <Package className="h-5 w-5 text-gray-400" />
        }
      </div>
      {/* Name – 2 Zeilen */}
      <span className="text-[11px] leading-tight line-clamp-2 font-medium text-gray-700 dark:text-gray-300 w-full text-center shrink-0">
        {prod.name}
      </span>
      {/* Label-Dots unten links */}
      <LabelDots labels={prod.labels} />
    </button>
  );
}

// ─── Zonen-Ansicht (read-only) ────────────────────────────────────────────────
function ZoneView({ zone, activeKey, products, onClickPdf }) {
  const innerCols = zone.inner_cols || 1;
  return (
    <div
      className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg overflow-hidden"
      style={{ gridColumn: `${zone.col_start} / span ${zone.col_span}` }}
      data-testid={`zone-view-${zone.id}`}
    >
      {/* Zonen-Header */}
      <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border-b dark:border-gray-700">
        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">{zone.name}</span>
        <span className="ml-2 text-xs text-muted-foreground">{zone.inner_cols}×{zone.inner_rows}</span>
      </div>

      {/* Scroll-Wrapper mit Fade-Hinweis */}
      <div className="relative">
        {innerCols > 4 && (
          <div
            className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 z-10"
            style={{ background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.92))' }}
            aria-hidden="true"
          />
        )}
        <div className="overflow-x-auto p-2">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${innerCols}, minmax(110px, 1fr))`,
              gap: '4px',
              minWidth: `${innerCols * 114}px`,
            }}
          >
            {Array.from({ length: zone.inner_rows }, (_, r) =>
              Array.from({ length: innerCols }, (_, c) => {
                const key = `${r}_${c}`;
                const pid = zone.slots?.[key]?.[activeKey] ?? null;
                return (
                  <div
                    key={key}
                    className="border border-gray-100 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700/30 overflow-hidden"
                    style={{ height: '7.5rem' }}
                  >
                    <ProductCellView productId={pid} products={products} onClickPdf={onClickPdf} />
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Vitrine-Ansicht (read-only) ──────────────────────────────────────────────
function VitrineView({ layout, products, activeKey, onClickPdf }) {
  const sorted = [...(layout.zones || [])].sort((a, b) => a.row_order - b.row_order);
  if (sorted.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground" data-testid="vitrine-empty">
        <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-30" />
        <p className="font-medium">Noch keine Zonen konfiguriert</p>
        <p className="text-sm mt-1">Admins können im Konfigurator-Modus Zonen hinzufügen.</p>
      </div>
    );
  }
  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(12, 1fr)' }} data-testid="vitrine-grid">
      {sorted.map(zone => (
        <ZoneView key={zone.id} zone={zone} activeKey={activeKey} products={products} onClickPdf={onClickPdf} />
      ))}
    </div>
  );
}

// ─── Visueller Produkt-Picker ────────────────────────────────────────────────
function ProductPicker({ value, onChange, products, label, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = products.find(p => p.id === value);
  const filtered = products.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase())
  );

  const getImgUrl = (p) => {
    if (p.image_url) return `${API}/api/backshop/products/${p.id}/image?token=${TOKEN()}`;
    if (p.thumbnail_url) return `${API}${p.thumbnail_url}?token=${TOKEN()}`;
    return null;
  };

  return (
    <div>
      <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-2 h-10 border border-input rounded-md px-2 text-sm bg-background dark:bg-gray-800 dark:border-gray-600 hover:border-amber-400 transition-colors text-left"
        data-testid={testId}
      >
        {selected ? (
          <>
            <div className="w-7 h-7 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden flex-shrink-0">
              {getImgUrl(selected)
                ? <img src={getImgUrl(selected)} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; }} />
                : <div className="w-full h-full flex items-center justify-center"><Package className="h-3.5 w-3.5 text-gray-400" /></div>
              }
            </div>
            <span className="flex-1 truncate text-gray-700 dark:text-gray-200">{selected.name}</span>
            <span
              role="button"
              tabIndex={0}
              onClick={e => { e.stopPropagation(); onChange(''); }}
              onKeyDown={e => e.key === 'Enter' && (e.stopPropagation(), onChange(''))}
              className="text-gray-400 hover:text-red-500 shrink-0 cursor-pointer"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          </>
        ) : (
          <span className="text-muted-foreground flex-1">– Leer –</span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-2 sm:p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-sm max-h-[70vh] flex flex-col shadow-2xl border dark:border-gray-700">
            <div className="flex items-center justify-between px-4 py-3 border-b dark:border-gray-700 shrink-0">
              <h4 className="font-semibold text-sm">{label}</h4>
              <button onClick={() => { setOpen(false); setSearch(''); }} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-3 py-2 border-b dark:border-gray-700 shrink-0">
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Produkt suchen..."
                className="w-full h-8 border border-input rounded px-3 text-sm bg-background dark:bg-gray-800 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400"
                autoFocus
              />
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              <button
                onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
                className="w-full text-left px-3 py-2 text-sm text-muted-foreground hover:bg-gray-100 dark:hover:bg-gray-800 rounded mb-2"
              >
                – Leer –
              </button>
              <div className="grid grid-cols-3 gap-2">
                {filtered.map(p => (
                  <button
                    key={p.id}
                    onClick={() => { onChange(p.id); setOpen(false); setSearch(''); }}
                    className={`flex flex-col items-center p-1.5 rounded-lg border text-xs text-center transition-colors ${
                      p.id === value
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-amber-400 hover:bg-amber-50/50 dark:hover:bg-amber-900/10'
                    }`}
                  >
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden mb-1 flex-shrink-0">
                      {getImgUrl(p)
                        ? <img src={getImgUrl(p)} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; }} />
                        : <div className="w-full h-full flex items-center justify-center"><Package className="h-5 w-5 text-gray-400" /></div>
                      }
                    </div>
                    <span className="line-clamp-2 leading-tight text-[11px]">{p.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Slot-Zuweisung Modal (Editor) ────────────────────────────────────────────
function SlotAssignModal({ slotKey, currentSlots, timeSlots, products, onSave, onClose }) {
  const [vals, setVals] = useState({
    morgens: currentSlots?.morgens ?? '',
    mittags: currentSlots?.mittags ?? '',
    abends:  currentSlots?.abends  ?? '',
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b dark:border-gray-700">
          <h3 className="font-semibold text-sm">Slot {slotKey} belegen</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {timeSlots.map(ts => (
            <ProductPicker
              key={ts.key}
              label={`${ts.label} (ab ${ts.starts_at})`}
              value={vals[ts.key] ?? ''}
              onChange={v => setVals(prev => ({ ...prev, [ts.key]: v || null }))}
              products={products}
              testId={`slot-assign-${ts.key}`}
            />
          ))}
        </div>
        <div className="flex gap-3 px-5 pb-5">
          <Button variant="outline" className="flex-1" onClick={onClose}>Abbrechen</Button>
          <Button className="flex-1 gap-2" onClick={() => onSave(vals)} data-testid="slot-assign-save">
            <Check className="h-4 w-4" /> Übernehmen
          </Button>
        </div>
      </div>
    </div>
  );
}

import { PRODUCT_LABELS } from '../pages/admin/BackshopAlleProdukte';

// ─── Label-Dots Komponente ─────────────────────────────────────────────────────
function LabelDots({ labels = [] }) {
  if (!labels?.length) return null;
  const matching = PRODUCT_LABELS.filter(l => labels.includes(l.key));
  if (!matching.length) return null;
  return (
    <div className="absolute bottom-1 left-1 flex gap-0.5 z-10">
      {matching.map(l => (
        <span
          key={l.key}
          title={l.label}
          className="w-2 h-2 rounded-full ring-1 ring-white/50 shadow-sm cursor-default"
          style={{ backgroundColor: l.color }}
        />
      ))}
    </div>
  );
}
function DndCell({ id, zoneId, hasAny, displayProd, imgUrl, onClickAssign, isDragOverlay }) {
  const {
    attributes, listeners, setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({ id, disabled: isDragOverlay });

  const { setNodeRef: setDropRef, isOver } = useDroppable({ id });
  const setRef = (el) => { setDragRef(el); setDropRef(el); };

  return (
    <div
      ref={setRef}
      data-testid={`slot-btn-${zoneId}-${id}`}
      title={displayProd?.name}
      style={hasAny ? { touchAction: 'none' } : undefined}
      className={`relative aspect-square rounded border overflow-hidden text-xs flex flex-col items-center justify-center transition-all select-none
        ${isDragging ? 'opacity-30 scale-95' : ''}
        ${isOver ? 'ring-2 ring-blue-400 border-blue-400 scale-105' : ''}
        ${hasAny
          ? 'border-amber-300 dark:border-amber-700 cursor-grab active:cursor-grabbing'
          : 'border-dashed border-gray-300 dark:border-gray-600 text-gray-300 dark:text-gray-600 cursor-pointer hover:border-amber-400 hover:text-amber-500'
        }`}
      onClick={() => !isDragging && onClickAssign()}
      {...(hasAny ? { ...attributes, ...listeners } : {})}
    >
      {imgUrl ? (
        <img src={imgUrl} alt={displayProd?.name} className="w-full h-full object-cover pointer-events-none" onError={e => { e.target.style.display='none'; }} />
      ) : hasAny ? (
        <div className="flex flex-col items-center gap-0.5 p-0.5 bg-amber-50 dark:bg-amber-900/20 w-full h-full justify-center">
          <Check className="h-3 w-3 text-amber-600 shrink-0" />
          {displayProd && <span className="text-[8px] leading-tight px-0.5 text-amber-700 dark:text-amber-400 line-clamp-1 w-full text-center">{displayProd.name.slice(0, 8)}</span>}
        </div>
      ) : (
        <span className="text-base leading-none">+</span>
      )}
      {/* Label-Dots auch im Editor */}
      {displayProd?.labels?.length > 0 && <LabelDots labels={displayProd.labels} />}
    </div>
  );
}

// ─── Zonen-Editor (in Editor-Modus) ──────────────────────────────────────────
function ZoneEditor({ zone, timeSlots, products, onChange, onDelete }) {
  const [assigningCell, setAssigningCell] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [activeDragId, setActiveDragId] = useState(null);

  const update = (fields) => onChange({ ...zone, ...fields });

  const handleGridSizeChange = (newCols, newRows) => {
    const newSlots = {};
    for (let r = 0; r < newRows; r++)
      for (let c = 0; c < newCols; c++) {
        const k = `${r}_${c}`;
        newSlots[k] = zone.slots?.[k] ?? { morgens: null, mittags: null, abends: null };
      }
    onChange({ ...zone, inner_cols: newCols, inner_rows: newRows, slots: newSlots });
  };

  // Drag & Drop Sensoren – PointerSensor für Maus, TouchSensor für Finger
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const handleDragStart = ({ active }) => setActiveDragId(active.id);

  const handleDragEnd = ({ active, over }) => {
    setActiveDragId(null);
    if (!over || active.id === over.id) return;

    const fromKey = active.id;
    const toKey = over.id;
    const fromSlot = zone.slots?.[fromKey] ?? {};
    const toSlot   = zone.slots?.[toKey]   ?? {};

    // Nur wenn die Quelle belegt ist, tauschen/verschieben
    const fromHasAny = Object.values(fromSlot).some(v => v);
    if (!fromHasAny) return;

    // Swap: Positionen tauschen
    const newSlots = {
      ...(zone.slots || {}),
      [fromKey]: toSlot,
      [toKey]: fromSlot,
    };
    onChange({ ...zone, slots: newSlots });
    toast.success('Positionen getauscht');
  };

  // Drag-Overlay: zeigt die gezogene Zelle als Vorschau
  const activePid = activeDragId
    ? (zone.slots?.[activeDragId]?.morgens || zone.slots?.[activeDragId]?.mittags || zone.slots?.[activeDragId]?.abends)
    : null;
  const activeProd = activePid ? products.find(p => p.id === activePid) : null;
  const activeImg  = activeProd?.image_url
    ? `${API}/api/backshop/products/${activeProd.id}/image?token=${TOKEN()}`
    : activeProd?.thumbnail_url
      ? `${API}${activeProd.thumbnail_url}?token=${TOKEN()}`
      : null;

  return (
    <div className="border dark:border-gray-700 rounded-lg overflow-hidden" data-testid={`zone-editor-${zone.id}`}>
      {/* Zone Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700/50">
        <GripVertical className="h-4 w-4 text-gray-400 shrink-0" />
        <input
          className="flex-1 bg-transparent text-sm font-medium focus:outline-none focus:ring-1 focus:ring-amber-400 rounded px-1"
          value={zone.name}
          onChange={e => update({ name: e.target.value })}
          placeholder="Zonenname"
          data-testid={`zone-name-${zone.id}`}
        />
        <button onClick={() => setExpanded(v => !v)} className="text-gray-400 hover:text-gray-600">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <button onClick={onDelete} className="text-red-400 hover:text-red-600" data-testid={`zone-delete-${zone.id}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {expanded && (
        <div className="p-3 space-y-3">
          {/* Grid-Position & Größe */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <label className="text-muted-foreground block mb-1">Start-Position (links → rechts)</label>
              <input type="number" min={1} max={12} value={zone.col_start}
                onChange={e => update({ col_start: Number(e.target.value) })}
                className="w-full h-7 rounded border border-input bg-background px-2 text-xs dark:bg-gray-800 dark:border-gray-600"
                data-testid={`zone-col-start-${zone.id}`}
              />
            </div>
            <div>
              <label className="text-muted-foreground block mb-1">Gesamtbreite der Reihe</label>
              <input type="number" min={1} max={13 - zone.col_start} value={zone.col_span}
                onChange={e => update({ col_span: Number(e.target.value) })}
                className="w-full h-7 rounded border border-input bg-background px-2 text-xs dark:bg-gray-800 dark:border-gray-600"
                data-testid={`zone-col-span-${zone.id}`}
              />
            </div>
            <div>
              <label className="text-muted-foreground block mb-1">Produkte nebeneinander</label>
              <input type="number" min={1} max={12} value={zone.inner_cols}
                onChange={e => handleGridSizeChange(Number(e.target.value), zone.inner_rows)}
                className="w-full h-7 rounded border border-input bg-background px-2 text-xs dark:bg-gray-800 dark:border-gray-600"
                data-testid={`zone-inner-cols-${zone.id}`}
              />
            </div>
            <div>
              <label className="text-muted-foreground block mb-1">Tiefe (von vorne → hinten)</label>
              <input type="number" min={1} max={8} value={zone.inner_rows}
                onChange={e => handleGridSizeChange(zone.inner_cols, Number(e.target.value))}
                className="w-full h-7 rounded border border-input bg-background px-2 text-xs dark:bg-gray-800 dark:border-gray-600"
                data-testid={`zone-inner-rows-${zone.id}`}
              />
            </div>
          </div>

          {/* Live-Vorschau */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
            <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
              <Eye className="h-3 w-3" /> Live-Vorschau
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${zone.inner_cols}, 1fr)`, gap: '3px' }}>
              {Array.from({ length: zone.inner_rows }, (_, r) =>
                Array.from({ length: zone.inner_cols }, (_, c) => (
                  <div
                    key={`${r}_${c}`}
                    className="bg-amber-100 dark:bg-amber-900/40 border border-amber-200 dark:border-amber-800 rounded-sm"
                    style={{ height: `${Math.max(16, Math.min(32, 80 / zone.inner_rows))}px` }}
                  />
                ))
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 text-center">
              {zone.inner_cols} × {zone.inner_rows} = <strong>{zone.inner_cols * zone.inner_rows}</strong> Produkte
            </p>
          </div>

          {/* Inner grid – Drag & Drop + click to assign */}
          <div>
            <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
              Produkte zuweisen
              <span className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded px-1.5 py-0.5 ml-1">
                Ziehen = tauschen · Klicken = belegen
              </span>
            </p>

            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${zone.inner_cols}, 1fr)`, gap: '3px' }}>
                {Array.from({ length: zone.inner_rows }, (_, r) =>
                  Array.from({ length: zone.inner_cols }, (_, c) => {
                    const k = `${r}_${c}`;
                    const slotData  = zone.slots?.[k] ?? {};
                    const hasAny    = Object.values(slotData).some(v => v);
                    const displayPid  = slotData.morgens || slotData.mittags || slotData.abends || null;
                    const displayProd = displayPid ? products.find(p => p.id === displayPid) : null;
                    const imgUrl = displayProd?.image_url
                      ? `${API}/api/backshop/products/${displayProd.id}/image?token=${TOKEN()}`
                      : displayProd?.thumbnail_url
                        ? `${API}${displayProd.thumbnail_url}?token=${TOKEN()}`
                        : null;
                    return (
                      <DndCell
                        key={k}
                        id={k}
                        zoneId={zone.id}
                        hasAny={hasAny}
                        displayProd={displayProd}
                        imgUrl={imgUrl}
                        onClickAssign={() => setAssigningCell(k)}
                      />
                    );
                  })
                )}
              </div>

              {/* Drag-Overlay: schwebt während dem Ziehen */}
              <DragOverlay dropAnimation={null}>
                {activeDragId && (
                  <div className="w-12 h-12 rounded border-2 border-blue-400 shadow-2xl overflow-hidden opacity-90 rotate-3 scale-110">
                    {activeImg ? (
                      <img src={activeImg} alt={activeProd?.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-amber-100 flex items-center justify-center">
                        <Check className="h-4 w-4 text-amber-600" />
                      </div>
                    )}
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          </div>
        </div>
      )}

      {assigningCell && (
        <SlotAssignModal
          slotKey={assigningCell}
          currentSlots={zone.slots?.[assigningCell] ?? {}}
          timeSlots={timeSlots}
          products={products}
          onSave={(vals) => {
            const newSlots = { ...(zone.slots || {}), [assigningCell]: vals };
            onChange({ ...zone, slots: newSlots });
            setAssigningCell(null);
          }}
          onClose={() => setAssigningCell(null)}
        />
      )}
    </div>
  );
}

// ─── Zeit-Slot Konfigurations-Panel ──────────────────────────────────────────
function TimeSlotConfigPanel({ timeSlots, onChange }) {
  return (
    <div className="border dark:border-gray-700 rounded-lg p-3 space-y-2">
      <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 flex items-center gap-1">
        <Clock className="h-3.5 w-3.5" /> Zeit-Slots konfigurieren
      </p>
      {timeSlots.map((ts, i) => (
        <div key={ts.key} className="grid grid-cols-2 gap-2">
          <input
            value={ts.label}
            onChange={e => { const s = [...timeSlots]; s[i] = { ...s[i], label: e.target.value }; onChange(s); }}
            className="h-7 rounded border border-input bg-background px-2 text-xs dark:bg-gray-800 dark:border-gray-600"
            placeholder="Label"
            data-testid={`ts-label-${ts.key}`}
          />
          <input
            type="time"
            value={ts.starts_at}
            onChange={e => { const s = [...timeSlots]; s[i] = { ...s[i], starts_at: e.target.value }; onChange(s); }}
            className="h-7 rounded border border-input bg-background px-2 text-xs dark:bg-gray-800 dark:border-gray-600"
            data-testid={`ts-time-${ts.key}`}
          />
        </div>
      ))}
    </div>
  );
}

// ─── Vitrine-Editor ───────────────────────────────────────────────────────────
function VitrineEditor({ layout, products, timeSlots, onSave, isSaving, onCancel }) {
  const [vitrineName, setVitrineName] = useState(layout.name || 'Vitrine');
  const [zones, setZones] = useState(layout.zones || []);
  const [editedTimeSlots, setEditedTimeSlots] = useState(timeSlots);

  const addZone = () => {
    const newZone = {
      id: `z_${Date.now()}`,
      name: `Neue Reihe`,
      col_start: 1,
      col_span: 12,
      row_order: zones.length,
      inner_cols: 4,
      inner_rows: 2,
      slots: buildEmptySlots(4, 2),
    };
    setZones(z => [...z, newZone]);
  };

  const updateZone = (idx, updated) => setZones(z => z.map((x, i) => i === idx ? updated : x));
  const deleteZone = (idx) => setZones(z => z.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4" data-testid="vitrine-editor">
      {/* Vitrine benennen */}
      <div className="border dark:border-gray-700 rounded-lg p-3 space-y-1">
        <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 flex items-center gap-1">
          <BookOpen className="h-3.5 w-3.5" /> Vitrine benennen
        </label>
        <input
          value={vitrineName}
          onChange={e => setVitrineName(e.target.value)}
          placeholder="z.B. Haupttheke, Regal rechts, Warmtheke..."
          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 dark:bg-gray-800 dark:border-gray-600"
          data-testid="vitrine-name-input"
        />
      </div>

      <TimeSlotConfigPanel timeSlots={editedTimeSlots} onChange={setEditedTimeSlots} />

      {zones.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg text-muted-foreground">
          <p className="text-sm">Noch keine Reihen. Erste Reihe hinzufügen:</p>
        </div>
      ) : (
        <div className="space-y-3">
          {zones.map((zone, idx) => (
            <ZoneEditor
              key={zone.id}
              zone={zone}
              timeSlots={editedTimeSlots}
              products={products}
              onChange={updated => updateZone(idx, updated)}
              onDelete={() => deleteZone(idx)}
            />
          ))}
        </div>
      )}

      <Button variant="outline" className="w-full gap-2 border-dashed" onClick={addZone} data-testid="add-zone-btn">
        <Plus className="h-4 w-4" /> Reihe hinzufügen
      </Button>

      <div className="flex gap-3 pt-2 sticky bottom-0 bg-background pb-2">
        <Button variant="outline" className="flex-1" onClick={onCancel} disabled={isSaving}>
          Abbrechen
        </Button>
        <Button
          className="flex-1 gap-2 bg-amber-600 hover:bg-amber-700"
          onClick={() => onSave({ zones, time_slots: editedTimeSlots, name: vitrineName })}
          disabled={isSaving}
          data-testid="vitrine-save-btn"
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Speichern
        </Button>
      </div>
    </div>
  );
}

// ─── Standort-Auswahl / Label ─────────────────────────────────────────────────
function LocationSelector({ locations, value, onChange }) {
  if (locations.length === 0) return null;
  const current = locations.find(l => l.id === value);
  // 1 Standort → nur Label, kein Dropdown
  if (locations.length === 1) {
    return (
      <div className="flex items-center gap-1.5 px-3 h-8 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-400">
        <MapPin className="h-3.5 w-3.5 shrink-0" />
        <span className="font-medium">{current?.name || '–'}</span>
      </div>
    );
  }
  // Mehrere Standorte → Dropdown
  return (
    <div className="flex items-center gap-1.5">
      <MapPin className="h-3.5 w-3.5 text-amber-500 shrink-0" />
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="h-8 rounded-md border border-input bg-background px-3 text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-amber-400 focus:outline-none"
        data-testid="location-selector"
      >
        {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
    </div>
  );
}

// ─── Vitrine-Editor (Admin/SL Tab "Vitrine anlegen") ─────────────────────────
export function VitrineEditorTab() {
  const [locations, setLocations] = useState([]);
  const [selectedLocId, setSelectedLocId] = useState('');
  const [layouts, setLayouts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // id der Vitrine im Bearbeiten-Modus
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/user/locations`, { headers: authH() }),
      axios.get(`${API}/api/backshop/products`, { headers: authH() }),
    ]).then(([lRes, pRes]) => {
      const locs = lRes.data || [];
      setLocations(locs);
      setProducts(pRes.data || []);
      if (locs.length > 0) setSelectedLocId(locs[0].id);
    }).catch(() => toast.error('Standorte konnten nicht geladen werden'));
  }, []);

  const fetchLayouts = useCallback(async (locId) => {
    if (!locId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/vitrine/layouts?location_id=${locId}`, { headers: authH() });
      setLayouts(res.data || []);
    } catch {
      toast.error('Vitrinen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedLocId) { setEditingId(null); fetchLayouts(selectedLocId); }
  }, [selectedLocId, fetchLayouts]);

  const handleCreate = async () => {
    const loc = locations.find(l => l.id === selectedLocId);
    if (!loc) return;
    setIsSaving(true);
    try {
      const body = { location_id: loc.id, location_name: loc.name, name: 'Neue Vitrine', time_slots: DEFAULT_TIME_SLOTS, zones: [] };
      const res = await axios.post(`${API}/api/vitrine/layouts`, body, { headers: authH() });
      setLayouts(prev => [...prev, res.data]);
      setEditingId(res.data.id);
      toast.success('Vitrine erstellt – jetzt konfigurieren');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async (layoutId, { zones, time_slots, name }) => {
    const existing = layouts.find(l => l.id === layoutId);
    if (!existing) return;
    setIsSaving(true);
    try {
      const body = { location_id: existing.location_id, location_name: existing.location_name, name: name || existing.name, time_slots, zones };
      const res = await axios.put(`${API}/api/vitrine/layouts/${layoutId}`, body, { headers: authH() });
      setLayouts(prev => prev.map(l => l.id === layoutId ? res.data : l));
      setEditingId(null);
      toast.success('Vitrine gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (layoutId) => {
    if (!window.confirm('Diese Vitrine wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/api/vitrine/layouts/${layoutId}`, { headers: authH() });
      setLayouts(prev => prev.filter(l => l.id !== layoutId));
      if (editingId === layoutId) setEditingId(null);
      toast.success('Vitrine gelöscht');
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="editor-loading">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="vitrine-editor-tab">
      {/* Standort-Header + Neue Vitrine */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <LocationSelector locations={locations} value={selectedLocId} onChange={id => setSelectedLocId(id)} />
        <Button
          size="sm"
          className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
          onClick={handleCreate}
          disabled={isSaving}
          data-testid="create-vitrine-btn"
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Neue Vitrine anlegen
        </Button>
      </div>

      {/* Vitrine-Liste */}
      {layouts.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl text-muted-foreground" data-testid="no-layouts">
          <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-20" />
          <p className="font-medium">Noch keine Vitrine für diesen Standort</p>
          <p className="text-sm mt-1">Klicken Sie auf "Neue Vitrine anlegen" um zu starten.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {layouts.map((lay, idx) => (
            <div key={lay.id} className="border dark:border-gray-700 rounded-xl overflow-hidden shadow-sm" data-testid={`vitrine-card-${lay.id}`}>
              {/* Card Header */}
              <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border-b dark:border-gray-700">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-gray-800 dark:text-gray-200 truncate">
                    {lay.name || `Vitrine ${idx + 1}`}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {(lay.zones || []).length} Reihen · {(lay.time_slots || DEFAULT_TIME_SLOTS).map(t => t.label).join(' / ')}
                  </p>
                </div>
                {editingId !== lay.id && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button variant="outline" size="sm" className="gap-1.5 h-7 text-xs" onClick={() => setEditingId(lay.id)} data-testid={`edit-vitrine-${lay.id}`}>
                      <Settings className="h-3.5 w-3.5" /> Bearbeiten
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => handleDelete(lay.id)} data-testid={`delete-vitrine-${lay.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Card Body: Editor oder Zusammenfassung */}
              {editingId === lay.id ? (
                <div className="p-4">
                  <VitrineEditor
                    layout={lay}
                    products={products}
                    timeSlots={lay.time_slots ?? DEFAULT_TIME_SLOTS}
                    onSave={(data) => handleSave(lay.id, data)}
                    isSaving={isSaving}
                    onCancel={() => setEditingId(null)}
                  />
                </div>
              ) : (
                <div className="px-4 py-3 space-y-1.5">
                  {(lay.zones || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">Noch keine Reihen – auf "Bearbeiten" klicken.</p>
                  ) : (
                    lay.zones.map(z => (
                      <div key={z.id} className="flex items-center gap-2 text-xs">
                        <div className="w-2 h-2 rounded-sm bg-amber-400 shrink-0" />
                        <span className="font-medium text-gray-700 dark:text-gray-300">{z.name}</span>
                        <span className="text-muted-foreground">· {z.inner_cols}×{z.inner_rows} Plätze</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Vitrine-Ansichts-Sektion (eine einzelne Vitrine, read-only) ──────────────
function VitrineViewSection({ layout, products }) {
  const timeSlots = layout.time_slots ?? DEFAULT_TIME_SLOTS;
  const zones = layout.zones || [];

  // Produkt-Anzahl pro Slot berechnen
  const slotCounts = {};
  for (const ts of timeSlots) {
    slotCounts[ts.key] = countSlotProducts(zones, ts.key);
  }

  // Intelligenter Start-Slot: zeitlich aktuell, aber nur wenn belegt
  const smartAuto = getSmartSlotKey(timeSlots, zones);
  const [activeSlot, setActiveSlot] = useState(null);
  const [viewingPdf, setViewingPdf] = useState(null); // eslint-disable-line no-unused-vars

  // Wenn der User manuell einen Slot gewählt hat, diesen nehmen –
  // aber prüfen ob er noch Produkte hat (könnte nach einem Edit leer werden)
  const resolvedSlot = (() => {
    if (activeSlot && slotCounts[activeSlot] > 0) return activeSlot;
    return smartAuto;
  })();

  return (
    <div className="space-y-3" data-testid={`vitrine-section-${layout.id}`}>
      {layout.name && (
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-amber-500 shrink-0" />
          <h2 className="text-base font-semibold text-gray-700 dark:text-gray-300">{layout.name}</h2>
        </div>
      )}
      <TimeSlotBar
        timeSlots={timeSlots}
        activeKey={resolvedSlot}
        slotCounts={slotCounts}
        onSelect={k => setActiveSlot(k)}
      />
      <VitrineView
        layout={layout}
        products={products}
        activeKey={resolvedSlot}
        onClickPdf={prod => {
          if (prod.pdf_url) window.open(`${API}${prod.pdf_url}?token=${TOKEN()}`, '_blank');
        }}
      />
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Clock className="h-3.5 w-3.5" />
        <span>
          Aktive Schicht: <strong>{timeSlots.find(t => t.key === resolvedSlot)?.label ?? resolvedSlot}</strong>
          {slotCounts[resolvedSlot] > 0 && (
            <span className="ml-1 text-gray-400">· {slotCounts[resolvedSlot]} Produkt{slotCounts[resolvedSlot] !== 1 ? 'e' : ''}</span>
          )}
        </span>
      </div>
      {viewingPdf && null /* PDF öffnet direkt via window.open */}
    </div>
  );
}

// ─── Vitrine-Ansicht (Read-Only für alle Rollen) ──────────────────────────────
export function VitrineViewTab({ user }) {
  const isAdmin = ['admin', 'stationsleitung', 'superadmin'].includes(user?.role);

  const [locations, setLocations] = useState([]);
  const [selectedLocId, setSelectedLocId] = useState('');
  const [layouts, setLayouts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/user/locations`, { headers: authH() }),
      axios.get(`${API}/api/backshop/products`, { headers: authH() }),
    ]).then(([lRes, pRes]) => {
      const locs = lRes.data || [];
      setLocations(locs);
      setProducts(pRes.data || []);
      // Mitarbeiter: auto zugewiesener Standort | Admin: erster Standort
      const empLocId = user?.location_ids?.[0];
      const locId = (!isAdmin && empLocId) ? empLocId : (locs[0]?.id || '');
      if (locId) setSelectedLocId(locId);
    }).catch(() => toast.error('Daten konnten nicht geladen werden'));
  }, [isAdmin, user]);

  const fetchLayouts = useCallback(async (locId) => {
    if (!locId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/vitrine/layouts?location_id=${locId}`, { headers: authH() });
      setLayouts(res.data || []);
    } catch {
      toast.error('Vitrine konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedLocId) fetchLayouts(selectedLocId); }, [selectedLocId, fetchLayouts]);

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="view-loading">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="thekenbelegung-view-tab">
      {/* Standort-Anzeige: Admin → Selector, Mitarbeiter → Label */}
      <LocationSelector
        locations={locations}
        value={selectedLocId}
        onChange={id => { setSelectedLocId(id); }}
      />

      {layouts.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl text-muted-foreground" data-testid="no-layout-view">
          <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-20" />
          <p className="font-medium">Noch keine Vitrine für diesen Standort konfiguriert</p>
          {isAdmin ? (
            <p className="text-sm mt-1 text-gray-400">Wechseln Sie zum Tab "Vitrine anlegen" um eine Belegung zu erstellen.</p>
          ) : (
            <p className="text-sm mt-1 text-gray-400">Ihr Administrator hat noch keine Vitrine für Ihren Standort erstellt.</p>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {layouts.map((lay, idx) => (
            <div key={lay.id}>
              {idx > 0 && <div className="border-t dark:border-gray-700" />}
              <VitrineViewSection layout={lay} products={products} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Legacy-Export (Rückwärtskompatibilität) ──────────────────────────────────
export function ThekenbelegungTab({ canWrite }) {
  return canWrite ? <VitrineEditorTab /> : <VitrineViewTab user={null} />;
}
